import { useApiQuery } from "@gladis/api-client";
import { StockDetail } from "@gladis/domain-investments/ui";
import type { CurrentUserDto, MemberDto } from "@gladis/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/investments_/$stockId")({
  component: Stock,
});

/**
 * One holding.
 *
 * The trailing underscore on the segment is TanStack's escape from layout nesting — the same shape
 * `properties_.$propertyId` and `savings_.$accountId` already use, so the detail view is a page in
 * its own right rather than something rendered inside the list.
 *
 * REMOVAL NAVIGATES BACK, because the record this page is about no longer exists. Staying would show
 * a detail view of a stock that is gone, which resolves to an error on the next refetch.
 */
function Stock(): ReactNode {
  const { stockId } = Route.useParams();
  const navigate = useNavigate();

  /*
    THE ONE REQUEST FEATURE 020 ADDS ANYWHERE.
 
    Savings, income, expenses, cards and settings all fetch the member list already, so faces there
    cost nothing. Investments never needed it — its rows name people by label alone — so this page
    is the exception FR-025 was amended to allow: one constant-cost fetch, never one per row.
 
    Shares its cache key with every other page's copy, so arriving here from the rail costs nothing
    at all.
  */
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;
  const members = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );

  return (
    <Page>
      <Page.Body>
        <div className="col-span-full flex flex-col gap-10">
          <StockDetail
            stockId={stockId}
            members={members.data?.members ?? []}
            onRemoved={() => {
              void navigate({ to: "/investments" });
            }}
          />
        </div>
      </Page.Body>
    </Page>
  );
}
